import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Fingerprint, ArrowRight, ShieldCheck, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";

const RISK_STYLES = {
  critical: { fg: "#991B1B", bg: "#FEE2E2", icon: ShieldAlert },
  high: { fg: "#92400E", bg: "#FEF3C7", icon: ShieldAlert },
  medium: { fg: "#1E40AF", bg: "#DBEAFE", icon: ShieldCheck },
  low: { fg: "#166534", bg: "#DCFCE7", icon: ShieldCheck },
};

export default function IdentityHealthCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/identity-governance/executive-widget")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
      <div className="rounded-2xl border border-[#E5E5E0] bg-white p-5" data-testid="identity-health-card">
        <div className="text-[11px] uppercase tracking-widest text-[#7E937D] mb-2">Identity Health</div>
        <div className="text-sm text-slate-400">Loading…</div>
      </div>
    );
  }

  const style = RISK_STYLES[data.risk_level] || RISK_STYLES.medium;
  const Icon = style.icon;

  return (
    <div className="rounded-2xl border border-[#E5E5E0] bg-white p-5" data-testid="identity-health-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Fingerprint className="w-4 h-4 text-[#1E3A8A]" />
          <div className="text-[11px] uppercase tracking-widest text-[#7E937D]">Identity Health</div>
        </div>
        <span
          style={{ color: style.fg, background: style.bg }}
          className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1"
          data-testid="identity-widget-risk"
        >
          <Icon className="w-3 h-3" /> {data.risk_level}
        </span>
      </div>

      <div className="flex items-baseline gap-3 mb-3">
        <div className="text-3xl font-semibold text-[#1E3A8A]" data-testid="identity-widget-score">
          {data.score}
        </div>
        <div className="text-xs text-slate-500">Identity security score</div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-xs">
        <div>
          <div className="text-slate-400 uppercase tracking-wider text-[10px]">Auth 24h</div>
          <div className="font-medium">{data.auth_24h.success}/{data.auth_24h.total}</div>
        </div>
        <div>
          <div className="text-slate-400 uppercase tracking-wider text-[10px]">MFA</div>
          <div className="font-medium">{data.mfa.adoption_pct}%</div>
        </div>
        <div>
          <div className="text-slate-400 uppercase tracking-wider text-[10px]">IdPs</div>
          <div className="font-medium">{data.active_idps}</div>
        </div>
      </div>

      {data.top_findings && data.top_findings.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#E5E5E0]">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Top findings</div>
          <ul className="space-y-1">
            {data.top_findings.slice(0, 3).map((f, i) => (
              <li key={i} className="text-xs flex items-center gap-2">
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: RISK_STYLES[f.severity]?.fg || "#64748B" }}
                />
                {f.title}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Link
        to="/identity-security"
        data-testid="identity-widget-view-all"
        className="mt-3 inline-flex items-center gap-1 text-xs text-[#1E3A8A] hover:underline"
      >
        Open Identity Security &amp; Governance
        <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}
